import { useState, useEffect, useCallback, useRef } from 'react';
import { getNextScheduledAnalysis } from '../services/api';
import { apiClient } from '../api/client';
import { getSocket } from '../utils/socketManager';
import { getSocketBaseUrl } from '../utils/socketUtils';

/**
 * Hook to fetch next scheduled analysis time for a specific view and pillar
 * Also listens to websockets for real-time processing status
 * @param {string} viewId - The view ID
 * @param {string} pillarId - The pillar ID
 * @param {Object} options - Optional parameters
 * @param {string|string[]} options.taskTypes - Task types to consider for processing detection (default: ['analysis'])
 * @param {boolean} options.enableScheduleFetch - Whether to fetch scheduled data (default: true)
 * @returns {Object} The scheduled analysis data, loading state, processing status, and time until next run
 */
export function useScheduledAnalysis(viewId, pillarId, options = {}) {
  const { taskTypes = ['analysis'], enableScheduleFetch = true } = options;
  const [scheduledData, setScheduledData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeUntilNext, setTimeUntilNext] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fetchInProgressRef = useRef(false);
  const timerRef = useRef(null);
  const prevParamsRef = useRef({ viewId: null, pillarId: null });
  const refetchTimeoutRef = useRef(null);
  const socketRef = useRef(null);
  const lastSchedulerStatusCheckAtRef = useRef(0);

  // Calculate time until next scheduled run
  const calculateTimeUntilNext = useCallback((nextRunAt) => {
    if (!nextRunAt) {
      return null;
    }
    
    const now = new Date();
    const scheduledTime = new Date(nextRunAt);
    const diffMs = scheduledTime - now;
    
    if (diffMs <= 0) {
      return null;
    }
    
    const diffSecs = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    const seconds = diffSecs % 60;
    
    const result = {
      total: diffMs,
      hours,
      minutes,
      seconds,
      formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    };
    
    return result;
  }, []);

  // Main fetch function - MOVED UP to fix hoisting issue
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!enableScheduleFetch) {
      setScheduledData(null);
      setTimeUntilNext(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (fetchInProgressRef.current || !viewId || !pillarId) {
      if (!viewId || !pillarId) {
        setScheduledData(null);
        setTimeUntilNext(null);
        setIsLoading(false);
        setError(null);
        setIsProcessing(false);
      }
      return;
    }

    fetchInProgressRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const result = await getNextScheduledAnalysis(viewId, pillarId);
      
      if (result.success && result.data) {
        setScheduledData(result.data);
        // Calculate initial time until next run
        const timeLeft = calculateTimeUntilNext(result.data.nextRunAt);
        setTimeUntilNext(timeLeft);
      } else {
        // No scheduled data found (batch might be disabled)
        setScheduledData(null);
        setTimeUntilNext(null);
        // Clear any running timers
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error fetching scheduled analysis:', err);
      setError(err.message);
      setScheduledData(null);
      setTimeUntilNext(null);
      // Clear any running timers on error
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [enableScheduleFetch, viewId, pillarId, calculateTimeUntilNext]);

  // Update time countdown every second
  const updateCountdown = useCallback(() => {
    if (!scheduledData?.nextRunAt) return;
    
    const timeLeft = calculateTimeUntilNext(scheduledData.nextRunAt);
    
    if (!timeLeft || timeLeft.total <= 0) {
      // Time has passed, schedule a refetch after a short delay
      setTimeUntilNext(null);
      
      // Clear any existing timeout
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      
      // Schedule refetch after 2 seconds to avoid hammering the API
      refetchTimeoutRef.current = setTimeout(() => {
        if (viewId && pillarId) {
          fetchData();
        }
      }, 2000);
      return;
    }
    
    setTimeUntilNext(timeLeft);
  }, [scheduledData?.nextRunAt, calculateTimeUntilNext, viewId, pillarId, fetchData]);

  // Check if the current task in scheduler status matches our view/pillar
  const isRelevantTask = useCallback((currentTask) => {
    const normalizedTypes = Array.isArray(taskTypes) ? taskTypes : [taskTypes];
    if (!currentTask || !currentTask.type || !normalizedTypes.includes(currentTask.type)) {
      return false;
    }

    // --- View Match ---
    // Handles cases where viewId is an ID (number) or ISO code (string)
    const viewMatchById = currentTask.view_id && viewId && String(currentTask.view_id) === String(viewId);
    const viewMatchByCode = currentTask.view_iso3 && viewId && currentTask.view_iso3.toLowerCase() === String(viewId).toLowerCase();
    const isViewMatch = viewMatchById || viewMatchByCode;

    // --- Pillar Match ---
    // Handles cases where pillarId is an ID (number) or key (string)
    const pillarMatchById = currentTask.pillar_id && pillarId && String(currentTask.pillar_id) === String(pillarId);
    const pillarMatchByKey = currentTask.pillar_key && pillarId && currentTask.pillar_key.toLowerCase() === String(pillarId).toLowerCase();
    const isPillarMatch = pillarMatchById || pillarMatchByKey;
    
    return isViewMatch && isPillarMatch;
  }, [taskTypes, viewId, pillarId]);

  // Check if activity update is for our view/pillar
  const isRelevantActivity = useCallback((activity) => {
    const normalizedTypes = Array.isArray(taskTypes) ? taskTypes : [taskTypes];
    if (!activity || !activity.type || !normalizedTypes.includes(activity.type)) return false;
    
    // Check if the activity matches our view and pillar
    const config = activity.config || {};
    const activityView = config.view;
    const activityPillar = config.pillar;
    
    // For view comparison, we need to handle both ID and code
    let isViewMatch = false;
    if (viewId && activityView) {
      // If activityView is a code (like 'LB'), we need to compare differently
      // For now, let's do a simple string comparison and also check if it's numeric
      isViewMatch = activityView === viewId || 
                      (typeof activityView === 'string' && activityView === String(viewId));
    }
    
    // For pillar comparison, activityPillar should be the pillar key
    const isPillarMatch = activityPillar && pillarId && 
                         (activityPillar === pillarId || String(activityPillar) === String(pillarId));
    
    return isViewMatch && isPillarMatch;
  }, [taskTypes, viewId, pillarId]);

  // Handle scheduler status updates (current running tasks)
  const handleSchedulerStatus = useCallback((data) => {
    if (!data) return;
    
    const statusData = data.type === 'scheduler_status' ? data.data : data;
    const isRunning = statusData.isRunning;
    const currentTask = statusData.currentTask;
    
    if (isRunning && currentTask && isRelevantTask(currentTask)) {
      setIsProcessing(true);
    } else {
      setIsProcessing(false);
    }
  }, [isRelevantTask]);

  // Handle websocket activity updates
  const handleActivityUpdate = useCallback((data) => {
    if (!data || !data.data) return;
    
    // Handle both single activity and array of activities
    const activities = Array.isArray(data.data) ? data.data : [data.data];
    
    for (const activity of activities) {
      if (isRelevantActivity(activity)) {
        // Check if this analysis is currently processing
        const isCurrentlyProcessing = activity.status === 'pending' || activity.status === 'processing';
        setIsProcessing(isCurrentlyProcessing);
        
        // If analysis completed, refetch scheduled data in case next run time changed
        if (activity.status === 'completed' || activity.status === 'failed') {
          // Clear processing state and refetch scheduled data after a short delay
          setTimeout(() => {
            setIsProcessing(false);
            if (viewId && pillarId) {
              fetchData();
            }
          }, 1000);
        }
        
        break; // Found relevant activity, no need to check others
      }
    }
  }, [isRelevantActivity, viewId, pillarId, fetchData]);

  // Handle batch configuration updates (when batches are enabled/disabled)
  const handleBatchUpdate = useCallback((data) => {
    if (!enableScheduleFetch) return;
    if (!data || !data.data) return;
    
    // If this is a batch update that affects our view/pillar, refetch scheduled data
    const batchData = data.data;
    
    // Check if this batch update affects our view/pillar combination
    if (batchData && batchData.views && batchData.pillars) {
      const affectsOurView = batchData.views.includes(parseInt(viewId, 10));
      const affectsOurPillar = batchData.pillars.includes(parseInt(pillarId, 10));
      const normalizedTypes = Array.isArray(taskTypes) ? taskTypes : [taskTypes];
      const batchTypes = Array.isArray(batchData.types) ? batchData.types : [];
      const affectsOurTypes = normalizedTypes.some((t) => batchTypes.includes(t));
      
      if (affectsOurView && affectsOurPillar && affectsOurTypes) {
        // This batch change affects our timer, refetch data
        if (process.env.NODE_ENV === 'development') {
          console.log('Batch configuration changed for our view/pillar, refetching scheduled data');
        }
        if (viewId && pillarId) {
          fetchData(true); // Force refresh
        }
      }
    }
  }, [enableScheduleFetch, taskTypes, viewId, pillarId, fetchData]);

  // Handle config updates (when batch configurations are enabled/disabled)
  const handleConfigUpdate = useCallback((data) => {
    if (!enableScheduleFetch) return;
    if (!data || !viewId || !pillarId) return;
    
    // When batch configs are updated, we should refetch to see if our schedule changed
    fetchData(true); // Force refresh
  }, [enableScheduleFetch, viewId, pillarId, fetchData]);

  // Check current scheduler status via API (backup for websocket)
  const checkCurrentSchedulerStatus = useCallback(async () => {
    if (!viewId || !pillarId) return;

    const now = Date.now();
    if (now - lastSchedulerStatusCheckAtRef.current < 5000) {
      return;
    }
    lastSchedulerStatusCheckAtRef.current = now;
    
    try {
      // Use the same apiClient as SchedulerStatusIndicator
      const response = await apiClient.get('/scheduler/status');
      
      if (response.data && response.data.data) {
        const statusData = response.data.data;
        
        // Check if there's a relevant task currently running
        if (statusData.isRunning && statusData.currentTask && isRelevantTask(statusData.currentTask)) {
          setIsProcessing(true);
        } else if (!statusData.isRunning) {
          // If scheduler is not running, make sure we clear processing state
          setIsProcessing(false);
        }
      }
    } catch (error) {
      // Silence error logging for performance
    }
  }, [viewId, pillarId, isRelevantTask]);

  // Setup websocket connection
  useEffect(() => {
    // Track handlers for cleanup
    let activeSocket = null;
    let handleConnect = null;
    let handleStatus = null;
    let handleDataUpdate = null;
    let handleBatch = null;
    let handleConfigs = null;

    // Clear any existing socket ref
    if (socketRef.current) {
      socketRef.current = null;
    }

    if (!viewId || !pillarId) return;

    const connectUrl = getSocketBaseUrl();
    if (!connectUrl) return;

    // Debounce connection to prevent rapid churn
    const timeoutId = setTimeout(() => {
      // Use shared socket manager
      const socket = getSocket('/batch-updates');
      if (!socket) return;

      activeSocket = socket;
      socketRef.current = socket;

      // Ensure socket is connected
      if (socket.disconnected) {
        socket.connect();
      }

      handleConnect = () => {
        // Request initial status when connected (use both methods like SchedulerStatusIndicator)
        socket.emit('request_data', {});
        socket.emit('request_status');
        
        // Also check status via HTTP as backup after a short delay
        setTimeout(() => {
          checkCurrentSchedulerStatus();
        }, 1000);
      };

      handleStatus = (data) => {
        handleSchedulerStatus(data);
      };

      handleDataUpdate = (data) => {
        if (data && data.type === 'scheduler_status') {
          handleSchedulerStatus(data);
        } else if (data && data.type === 'activity_update') {
          handleActivityUpdate(data);
        } else if (data && data.type === 'batch_update') {
          handleBatchUpdate(data);
        } else if (data && data.type === 'configs_update') {
          handleConfigUpdate(data);
        }
      };

      handleBatch = (data) => handleBatchUpdate(data);
      handleConfigs = (data) => handleConfigUpdate(data);

      if (socket.connected) {
        handleConnect();
      }

      socket.on('connect', handleConnect);
      socket.on('scheduler_status', handleStatus);
      socket.on('data_update', handleDataUpdate);
      socket.on('batch_update', handleBatch);
      socket.on('configs_update', handleConfigs);

    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timeoutId);
      if (activeSocket) {
        if (handleConnect) activeSocket.off('connect', handleConnect);
        if (handleStatus) activeSocket.off('scheduler_status', handleStatus);
        if (handleDataUpdate) activeSocket.off('data_update', handleDataUpdate);
        if (handleBatch) activeSocket.off('batch_update', handleBatch);
        if (handleConfigs) activeSocket.off('configs_update', handleConfigs);
      }
      socketRef.current = null;
    };
  }, [viewId, pillarId, handleSchedulerStatus, handleActivityUpdate, handleBatchUpdate, handleConfigUpdate, checkCurrentSchedulerStatus]);

  // Fetch data when parameters change
  useEffect(() => {
    const prevParams = prevParamsRef.current;
    const normalizedViewId = viewId != null ? String(viewId) : null;
    const normalizedPillarId = pillarId != null ? String(pillarId) : null;
    const paramsChanged = prevParams.viewId !== normalizedViewId || prevParams.pillarId !== normalizedPillarId;
    
    if (paramsChanged) {
      prevParamsRef.current = { viewId: normalizedViewId, pillarId: normalizedPillarId };
      setError(null);
      setScheduledData(null);
      setTimeUntilNext(null);
      setIsProcessing(false);
      
      if (viewId && pillarId) {
        // Check current scheduler status immediately to preserve processing state on refresh
        checkCurrentSchedulerStatus();
        // Then fetch scheduled data
        if (enableScheduleFetch) {
          fetchData();
        } else {
          setIsLoading(false);
          setScheduledData(null);
          setTimeUntilNext(null);
        }
      } else {
        setIsLoading(false);
      }
    }
  }, [enableScheduleFetch, viewId, pillarId, fetchData, checkCurrentSchedulerStatus]);

  // Set up countdown timer
  useEffect(() => {
    // Always clear the existing timer before starting a new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (scheduledData?.nextRunAt && !isProcessing) {
      // Perform an initial update immediately
      updateCountdown();
      // Set up a single, simple interval to update every second
      timerRef.current = setInterval(updateCountdown, 1000);
    } else {
      // If there's no schedule or it's currently processing, ensure the timer is stopped
      // and the countdown display is cleared.
      if (!scheduledData && !isProcessing) {
        setTimeUntilNext(null);
      }
    }

    // Cleanup function to clear the interval when the component unmounts or dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [scheduledData?.nextRunAt, isProcessing, updateCountdown]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  return {
    scheduledData,
    isLoading,
    error,
    timeUntilNext,
    isProcessing,
    refreshData: () => fetchData(true) // Allow external components to force refresh
  };
} 